import React, {useCallback, useEffect, useState} from 'react';
import {View, ScrollView, StyleSheet, RefreshControl, Alert, Image} from 'react-native';
import {
  Text, ActivityIndicator,
  Dialog, Portal, Button, TextInput, IconButton, Snackbar, Chip, Divider, FAB,
} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import GradientHeader from '../../components/common/GradientHeader';
import {useNavigation} from '@react-navigation/native';
import {measurementApi} from '../../services/api/measurementApi';
import {colors, spacing} from '../../theme';

const GENDER_OPTIONS = ['BOTH', 'MALE', 'FEMALE'];

const CatalogItemListScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();

  const [catalogItems, setCatalogItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [dressName, setDressName] = useState('');
  const [gender, setGender] = useState('BOTH');
  const [snackbar, setSnackbar] = useState('');
  const [fabOpen, setFabOpen] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const response = await measurementApi.listCatalogItems(true);
      setCatalogItems(response.data.data);
    } catch (e) {
      setSnackbar(e.response?.data?.message || t('common.error'));
    }
  }, [t]);

  useEffect(() => {
    setIsLoading(true);
    fetchData().finally(() => setIsLoading(false));
  }, [fetchData]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
  }, [fetchData]);

  const openAddDialog = useCallback(() => {
    setEditingItem(null);
    setDressName('');
    setGender('BOTH');
    setDialogVisible(true);
  }, []);

  const openEditDialog = useCallback((item) => {
    setEditingItem(item);
    setDressName(item.name);
    setGender(item.gender || 'BOTH');
    setDialogVisible(true);
  }, []);

  const handleSave = useCallback(async () => {
    const name = dressName.trim();
    if (!name) return;

    try {
      if (editingItem) {
        await measurementApi.updateCatalogItem(editingItem.id, {name, gender});
      } else {
        const code = name.toUpperCase().replace(/[^A-Z0-9]+/g, '_');
        await measurementApi.createCatalogItem({name, code, gender});
      }
      setDialogVisible(false);
      setDressName('');
      setEditingItem(null);
      await fetchData();
    } catch (e) {
      setSnackbar(e.response?.data?.error?.message || t('common.error'));
    }
  }, [dressName, gender, editingItem, fetchData, t]);

  const handleToggle = useCallback(async (item, newValue) => {
    try {
      await measurementApi.toggleCatalogItem(item.id, {isEnabled: newValue});
      await fetchData();
    } catch (e) {
      setSnackbar(e.response?.data?.message || t('common.error'));
    }
  }, [fetchData, t]);

  const handleDelete = useCallback((item) => {
    Alert.alert(
      t('common.delete'),
      `${t('common.delete')} "${item.name}"?`,
      [
        {text: t('common.cancel'), style: 'cancel'},
        {
          text: t('common.delete'),
          style: 'destructive',
          onPress: async () => {
            try {
              await measurementApi.deleteCatalogItem(item.id);
              await fetchData();
            } catch (e) {
              setSnackbar(e.response?.data?.error?.message || t('common.error'));
            }
          },
        },
      ],
    );
  }, [fetchData, t]);

  if (isLoading && catalogItems.length === 0) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <GradientHeader title={t('measurement.catalogItems')} onBack={() => navigation.goBack()} />

      <ScrollView
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} colors={[colors.primary]} />}>
        <View style={styles.descriptionRow}>
          <Text style={styles.descriptionText}>
            {t('measurement.descriptionText')}
          </Text>
        </View>
        <Divider />

        {catalogItems.length === 0 ? (
          <Text style={styles.emptyText}>{t('measurement.noCustomCatalogItems')}</Text>
        ) : (
          catalogItems.slice().sort((a, b) => (b.enabled ? 1 : 0) - (a.enabled ? 1 : 0)).map((dt, index) => (
            <View key={dt.id}>
              <View style={[styles.row, !dt.enabled && styles.rowDisabled]}>
                <IconButton
                  icon={dt.enabled ? 'check-circle' : 'circle-outline'}
                  size={24}
                  iconColor={dt.enabled ? colors.primary : colors.textSecondary}
                  onPress={() => handleToggle(dt, !dt.enabled)}
                  style={styles.toggleBtn}
                />
                {dt.mainImageUrl ? (
                  <View style={styles.avatarImageContainer}>
                    <Image source={{uri: dt.mainImageUrl}} style={styles.avatarImage} />
                  </View>
                ) : (
                  <View style={styles.avatarPlaceholder}>
                    <Text style={styles.avatarPlaceholderText}>{dt.name.charAt(0).toUpperCase()}</Text>
                  </View>
                )}
                <View style={styles.rowText}>
                  <Text style={[styles.dtName, !dt.enabled && styles.textDisabled]}>{dt.name}</Text>
                </View>
                <IconButton
                  icon="tape-measure"
                  size={20}
                  iconColor={colors.secondary}
                  onPress={() => navigation.navigate('CatalogItemMeasurements', {
                    catalogItemId: dt.id,
                    catalogItemName: dt.name,
                  })}
                  style={styles.actionBtn}
                />
                <IconButton
                  icon="content-cut"
                  size={20}
                  iconColor={colors.secondary}
                  onPress={() => navigation.navigate('CatalogItemOptionTypes', {
                    catalogItemId: dt.id,
                    catalogItemName: dt.name,
                  })}
                  style={styles.actionBtn}
                />
                <IconButton
                  icon="pencil-outline"
                  size={20}
                  iconColor={colors.textSecondary}
                  onPress={() => openEditDialog(dt)}
                  style={styles.actionBtn}
                />
                {!dt.predefined && (
                  <IconButton
                    icon="delete-outline"
                    size={20}
                    iconColor={colors.error}
                    onPress={() => handleDelete(dt)}
                    style={styles.actionBtn}
                  />
                )}
              </View>
              {index < catalogItems.length - 1 && <Divider />}
            </View>
          ))
        )}
      </ScrollView>

      <Portal>
        <Dialog visible={dialogVisible} onDismiss={() => setDialogVisible(false)} style={{borderRadius: 15}}>
          <Dialog.Title>
            {editingItem ? t('measurement.editCatalogItem') : t('measurement.addCatalogItem')}
          </Dialog.Title>
          <Dialog.Content>
            <TextInput
              label={t('measurement.dressName')}
              value={dressName}
              onChangeText={setDressName}
              mode="outlined"
              style={styles.dialogInput}
            />
            <Text variant="titleSmall" style={styles.chipLabel}>
              {t('measurement.gender')}
            </Text>
            <View style={styles.chipRow}>
              {GENDER_OPTIONS.map(opt => (
                <Chip
                  key={opt}
                  selected={gender === opt}
                  selectedColor={gender === opt ? '#fff' : undefined}
                  onPress={() => setGender(opt)}
                  style={[styles.optChip, gender === opt && styles.optChipActive]}>
                  <Text style={{fontSize: 13, color: gender === opt ? '#fff' : '#000', marginTop: 3}}>{opt === 'BOTH' ? t('measurement.both') : opt === 'MALE' ? t('customer.male') : t('customer.female')}</Text>
                </Chip>
              ))}
            </View>
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setDialogVisible(false)}>{t('common.cancel')}</Button>
            <Button mode="contained" onPress={handleSave} style={styles.saveButton}>
              {t('common.save')}
            </Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>

      <FAB.Group
        open={fabOpen}
        visible
        icon={fabOpen ? 'close' : 'plus'}
        actions={[
          {
            icon: 'plus',
            label: t('measurement.addCatalogItem'),
            onPress: openAddDialog,
          },
          {
            icon: 'database-sync',
            label: t('measurement.loadSeedData'),
            onPress: async () => {
              try {
                const response = await measurementApi.syncSeedData();
                setSnackbar(response.data.data);
                await fetchData();
              } catch (e) {
                setSnackbar(e.response?.data?.error?.message || t('common.error'));
              }
            },
          },
        ]}
        onStateChange={({open}) => setFabOpen(open)}
        fabStyle={styles.fab}
        color={colors.white}
      />

      <Snackbar visible={!!snackbar} onDismiss={() => setSnackbar('')} duration={3000}>
        {snackbar}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  loadingContainer: {flex: 1, justifyContent: 'center', alignItems: 'center'},
  descriptionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.md,
    backgroundColor: colors.surface,
  },
  descriptionText: {
    flex: 1,
    fontSize: 14,
    color: colors.textSecondary,
    lineHeight: 20,
  },
  fab: {
    backgroundColor: colors.primary,
  },
  emptyText: {
    textAlign: 'center',
    color: colors.textSecondary,
    marginTop: spacing.xxl,
    paddingHorizontal: spacing.md,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.surface,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
  },
  rowDisabled: {opacity: 0.5},
  rowText: {flex: 1},
  dtName: {fontSize: 16, fontWeight: '600', color: colors.text},
  textDisabled: {color: colors.textSecondary},
  badges: {flexDirection: 'row', gap: spacing.xs, marginTop: 2},
  genderChip: {height: 22, paddingVertical: 0, backgroundColor: colors.primary + '15'},
  avatarImageContainer: {
    width: 36, height: 36, borderRadius: 18, marginRight: spacing.sm,
    padding: 3, justifyContent: 'center', alignItems: 'center',
    borderWidth: 1, borderColor: colors.border,
  },
  avatarImage: {width: 28, height: 28, borderRadius: 14},
  avatarPlaceholder: {
    width: 36, height: 36, borderRadius: 18, marginRight: spacing.sm,
    backgroundColor: colors.primary + '30', justifyContent: 'center', alignItems: 'center',
  },
  avatarPlaceholderText: {fontSize: 15, fontWeight: '600', color: colors.primary},
  toggleBtn: {margin: 0},
  actionBtn: {margin: 0},
  dialogInput: {marginBottom: spacing.md, backgroundColor: colors.surface},
  chipLabel: {color: colors.text, marginBottom: spacing.xs},
  chipRow: {flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs},
  optChip: {backgroundColor: colors.background},
  optChipActive: {backgroundColor: colors.primary},
  saveButton: {backgroundColor: colors.primary, borderRadius: 10, paddingHorizontal: 10},
});

export default CatalogItemListScreen;
